import { ReactElement, useState } from 'react'
import styled from '@emotion/styled'
import _ from 'lodash'
import { saveAs } from 'file-saver'

// utils
import { getFileNameAndExt, getReadableBytes } from 'helpers/utils'
import { useStaticResourceStateValue } from 'contexts'
import { showError } from 'helpers/message'

// components
import { IconButton, Tooltip } from 'components/common'

// constants
import { FILE_UPLOAD_STATUS } from 'constants/fileUpload'
import { TOOLTIP_PLACEMENT } from 'constants/settings'

import scss from './index.module.scss'
import FileUploadingStatusIcon from '../FileUploadingStatusIcon'

/** px */
export const FILE_ITEM_FIXED_WIDTH = 214

const LOADING_ICON_SIZE = 12

const StyledUploadedFilePreview = styled.div<{
  hasError?: boolean
  isDeleted?: boolean
}>`
  display: flex;
  justify-content: space-between;
  align-items: start;
  width: ${FILE_ITEM_FIXED_WIDTH}px;
  padding: 8px;
  background: #fafafa81;
  border: 1px solid
    ${({ hasError, theme }) => (hasError ? theme.danger : '#eaeaea')};
  opacity: ${({ isDeleted }) => (isDeleted ? 0.5 : 1)};
  border-radius: 5px;
  overflow-wrap: anywhere;
  word-break: normal;
`

const FileItem = ({
  className = '',
  name,
  path,
  size,
  downloadUrl,
  uploadingStatus,
  hasError,
  isDeleted,
  disabled,
  onDelete,
}: {
  className?: string
  name: string
  path?: string
  size?: number
  downloadUrl?: string
  uploadingStatus?: keyof typeof FILE_UPLOAD_STATUS
  hasError?: boolean
  isDeleted?: boolean
  disabled?: boolean
  onDelete?: (name: string) => void
}): ReactElement => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [filename, ext] = getFileNameAndExt(name)
  const newName = `${_.truncate(filename, {
    length: 18,
    separator: ' ',
  })}.${ext}`

  const { Icons } = useStaticResourceStateValue()

  return (
    <StyledUploadedFilePreview
      className={className}
      key={path || name}
      hasError={hasError}
      isDeleted={isDeleted}
    >
      <div className='d-flex'>
        <div style={{ marginTop: '5px' }}>
          <Icons.SpreadsheetIcon />
        </div>
        <div className={scss.filePreviewName}>
          <div className='defaultText'>{newName}</div>
          {_.isNumber(size) && <div>{getReadableBytes(size)}</div>}
        </div>
      </div>

      <div className='h-100 d-flex flex-column justify-content-between align-items-center'>
        <div className='d-flex flex-row align-items-center'>
          {downloadUrl && (
            <Tooltip
              placement={TOOLTIP_PLACEMENT.top}
              overlay={
                <span>{isDownloading ? 'Downloading...' : 'Download'}</span>
              }
            >
              <IconButton
                icon={isDownloading ? 'ButtonLoaderIcon' : 'FaDownload'}
                className={`${scss.iconButton} ${
                  isDownloading ? 'fa-spin' : ''
                }`}
                disabled={isDownloading}
                {...(isDownloading && {
                  size: LOADING_ICON_SIZE,
                  width: LOADING_ICON_SIZE,
                  height: LOADING_ICON_SIZE,
                })}
                onClick={async e => {
                  e.preventDefault()
                  setIsDownloading(true)

                  try {
                    const response = await fetch(downloadUrl)
                    const blob = await response.blob()
                    saveAs(blob, name)
                  } catch (error) {
                    const errorMessage = (error as Error)?.message
                    showError(
                      `Error downloading file${
                        errorMessage && `: ${errorMessage}`
                      }`
                    )
                  } finally {
                    setIsDownloading(false)
                  }
                }}
              />
            </Tooltip>
          )}

          {onDelete && (
            <Tooltip
              placement={TOOLTIP_PLACEMENT.top}
              overlay={<span>{isDeleted ? 'Restore' : 'Delete'}</span>}
            >
              <IconButton
                className={scss.iconButton}
                icon={isDeleted ? 'UndoIcon' : 'FiX'}
                disabled={disabled}
                onClick={() => {
                  onDelete?.(name)
                }}
              />
            </Tooltip>
          )}
        </div>

        {uploadingStatus && (
          <FileUploadingStatusIcon uploadingStatus={uploadingStatus} />
        )}
      </div>
    </StyledUploadedFilePreview>
  )
}

export default FileItem
